import React from 'react'
import { Search, X } from 'lucide-react'

import { normalizeAdminReportStudentName } from '../lib/adminReportFilters'

const ReportStudentAutocomplete = ({
  value = '',
  onChange,
  options = [],
  placeholder = 'Buscar aluno por nome',
  emptyLabel = 'Nenhum aluno encontrado',
  clearAriaLabel = 'Limpar busca de aluno',
}) => {
  const containerRef = React.useRef(null)
  const [open, setOpen] = React.useState(false)
  const [activeIndex, setActiveIndex] = React.useState(-1)

  React.useEffect(() => {
    const handlePointerDown = (event) => {
      if (!containerRef.current?.contains(event.target)) {
        setOpen(false)
      }
    }

    document.addEventListener('mousedown', handlePointerDown)
    return () => document.removeEventListener('mousedown', handlePointerDown)
  }, [])

  const normalizedValue = normalizeAdminReportStudentName(value)
  const filteredOptions = React.useMemo(() => {
    const normalizedOptions = Array.isArray(options) ? options : []
    if (!normalizedValue) return normalizedOptions.slice(0, 8)

    return normalizedOptions.filter((option) => (
      normalizeAdminReportStudentName(option?.name).includes(normalizedValue)
    )).slice(0, 8)
  }, [options, normalizedValue])

  React.useEffect(() => {
    if (!open || filteredOptions.length === 0) {
      setActiveIndex(-1)
      return
    }
    setActiveIndex(-1)
  }, [filteredOptions, open])

  const shouldShowDropdown = open && filteredOptions.length > 0
  const shouldShowEmptyState = open && normalizedValue && filteredOptions.length === 0

  return (
    <div ref={containerRef} className="relative">
      <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
      <input
        type="text"
        value={value}
        onFocus={() => setOpen(true)}
        onChange={(event) => {
          onChange?.(event.target.value)
          setOpen(true)
        }}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            setOpen(false)
            setActiveIndex(-1)
            return
          }

          if (!shouldShowDropdown) return

          if (event.key === 'ArrowDown') {
            event.preventDefault()
            setActiveIndex((current) => (
              current < filteredOptions.length - 1 ? current + 1 : 0
            ))
          }

          if (event.key === 'ArrowUp') {
            event.preventDefault()
            setActiveIndex((current) => (
              current > 0 ? current - 1 : filteredOptions.length - 1
            ))
          }

          if (event.key === 'Enter' && activeIndex >= 0) {
            event.preventDefault()
            onChange?.(filteredOptions[activeIndex]?.name || '')
            setOpen(false)
          }
        }}
        placeholder={placeholder}
        autoComplete="off"
        className="w-full bg-slate-950 border border-slate-800 rounded-lg pl-12 pr-12 py-3 text-sm text-white outline-none focus:border-slate-600 focus:ring-1 focus:ring-primary/40 transition min-h-[48px] hover:border-slate-700 placeholder:text-slate-600"
      />
      {value ? (
        <button
          type="button"
          onClick={() => {
            onChange?.('')
            setOpen(false)
          }}
          className="absolute right-3 top-1/2 -translate-y-1/2 inline-flex h-7 w-7 items-center justify-center rounded-full text-slate-500 transition hover:bg-slate-800 hover:text-white"
          aria-label={clearAriaLabel}
        >
          <X size={14} />
        </button>
      ) : null}

      {shouldShowDropdown ? (
        <div className="absolute mt-2 w-full overflow-hidden rounded-xl border border-slate-700 bg-slate-950 shadow-2xl z-40 ring-1 ring-black/50">
          <div className="max-h-72 overflow-auto py-1">
            {filteredOptions.map((option) => (
              <button
                key={option.id}
                type="button"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => {
                  onChange?.(option.name)
                  setOpen(false)
                }}
                className={`flex w-full items-center px-4 py-3 text-left text-sm transition ${
                  filteredOptions[activeIndex]?.id === option.id
                    ? 'bg-slate-800/90 text-white'
                    : 'text-slate-200 hover:bg-slate-800/70'
                }`}
              >
                <span className="truncate">{option.name}</span>
              </button>
            ))}
          </div>
        </div>
      ) : null}

      {shouldShowEmptyState ? (
        <div className="absolute mt-2 w-full rounded-xl border border-slate-700 bg-slate-950 px-4 py-3 text-sm text-slate-500 shadow-2xl z-40 ring-1 ring-black/50">
          {emptyLabel}
        </div>
      ) : null}
    </div>
  )
}

export default ReportStudentAutocomplete
